import React, { useState } from 'react';

interface ContactInfo {
  email: string;
  name?: string;
  phone?: string;
  gdpr_consent: boolean;
  marketing_consent: boolean;
}

interface ContactFormProps {
  onSubmit: (contactInfo: ContactInfo) => void;
  onBack: () => void;
}

export const ContactForm: React.FC<ContactFormProps> = ({ onSubmit, onBack }) => {
  const [formData, setFormData] = useState<ContactInfo>({
    email: '',
    name: '',
    phone: '',
    gdpr_consent: false,
    marketing_consent: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email je povinný';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Zadejte platný email';
    }

    // GDPR consent validation
    if (!formData.gdpr_consent) {
      newErrors.gdpr_consent = 'Souhlas se zpracováním osobních údajů je povinný';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof ContactInfo, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  return (
    <div className="contact-card">
      {/* Header */}
      <div className="contact-header">
        <h2 className="contact-title">
          Získej svůj osobní plán rozvoje zdarma
        </h2>
        <p className="contact-subtitle">
          Email potřebujeme, abychom ti mohli poslat tvůj personalizovaný plán rozvoje.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="contact-form">
        {/* Email Field */}
        <div className="contact-field">
          <label htmlFor="email" className="contact-label">
            Email *
          </label>
          <input
            type="email"
            id="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`contact-input ${errors.email ? 'has-error' : ''}`}
            placeholder="vas@email.cz"
          />
          {errors.email && (
            <p className="contact-error">{errors.email}</p>
          )}
        </div>

        {/* Name Field */}
        <div className="contact-field">
          <label htmlFor="name" className="contact-label">
            Jméno (volitelné)
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="contact-input"
            placeholder="Vaše jméno"
          />
        </div>

        {/* Phone Field */}
        <div className="contact-field">
          <label htmlFor="phone" className="contact-label">
            Telefon (volitelné)
          </label>
          <input
            type="tel"
            id="phone"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className="contact-input"
            placeholder="+420 123 456 789"
          />
        </div>

        {/* GDPR Consent */}
        <div className="contact-field">
          <label className={`contact-consent-row ${formData.gdpr_consent ? 'is-checked' : ''}`}>
            <input
              type="checkbox"
              checked={formData.gdpr_consent}
              onChange={(e) => handleInputChange('gdpr_consent', e.target.checked)}
              className="contact-checkbox-input"
            />
            <div className="flex-1">
              <span className="contact-checkbox-text">
                Souhlasím se zpracováním osobních údajů pro účely poskytnutí personalizovaného plánu rozvoje
                a vytvoření trvalého odkazu k výsledkům. Údaje uchováváme po dobu 2 let. *
              </span>
              {errors.gdpr_consent && (
                <p className="contact-error">{errors.gdpr_consent}</p>
              )}
            </div>
          </label>
        </div>

        {/* Marketing Consent */}
        <div className="contact-field">
          <label className={`contact-consent-row ${formData.marketing_consent ? 'is-checked' : ''}`}>
            <input
              type="checkbox"
              checked={formData.marketing_consent}
              onChange={(e) => handleInputChange('marketing_consent', e.target.checked)}
              className="contact-checkbox-input"
            />
            <span className="contact-checkbox-text">
              Souhlasím s odesíláním marketingových komunikací a tipů pro osobní rozvoj (můžete kdykoli odhlásit).
            </span>
          </label>
        </div>

        {/* Navigation Buttons */}
        <div className="contact-actions">
          <button
            type="button"
            onClick={onBack}
            className="question-btn question-btn-secondary"
          >
            ← Zpět
          </button>

          <button
            type="submit"
            className="question-btn question-btn-primary"
          >
            Získat plán zdarma →
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
